import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Icon } from '@iconify/react';
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import AddVenueLayer from "../components/AddVenueLayer";
import VenueGalleryManager from "../components/VenueGalleryManager";
import VenueMenuManager from "../components/VenueMenuManager";
import VenueFeaturesManager from "../components/VenueFeaturesManager";
import VenueBlogsManager from "../components/VenueBlogsManager";
import VenueVouchersManager from "../components/VenueVouchersManager";
import VenuePreviewModal from "../components/VenuePreviewModal";
import { useAuth } from "../context/AuthContext";
import { toast } from "react-toastify";

const AddVenuePage = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user } = useAuth();

    const activeTab = searchParams.get('tab') || 'details';
    const [createdVenueId, setCreatedVenueId] = useState(null);
    const [showPreview, setShowPreview] = useState(false);

    const handleTabChange = (tabKey) => {
        setSearchParams({ tab: tabKey });
        if (tabKey !== 'details' && !createdVenueId) {
            const selectedTab = tabs.find((tab) => tab.id === tabKey);
            toast.info(
                `${selectedTab?.label || 'This section'} is locked. Complete and save Listing Details first.`,
                { toastId: `locked-venue-tab-${tabKey}` }
            );
        }
    };

    const handleSuccess = () => {
        navigate('/my-venues');
    };

    const tabs = [
        { id: 'details', label: 'Listing Details', icon: 'solar:pen-bold' },
        { id: 'gallery', label: 'Gallery Media', icon: 'solar:gallery-linear' },
        { id: 'menu', label: 'Menu Items', icon: 'solar:chef-hat-linear' },
        { id: 'features', label: 'Features & Offers', icon: 'solar:star-linear' },
        { id: 'blogs', label: 'Blog Posts & Events', icon: 'solar:document-text-linear' },
        { id: 'vouchers', label: 'Vouchers & Deals', icon: 'solar:ticket-sale-linear' }
    ];

    const tabDescriptions = {
        gallery: {
            title: "Gallery Media Photos",
            icon: "solar:gallery-bold",
            color: "text-primary-600",
            bg: "bg-primary-50",
            border: "border-primary-100",
            summary: "Showcase your dining rooms, patio seating, signature dishes, and venue ambience with high-resolution photos.",
            items: [
                "Upload multiple high-res photos (up to 10MB each)",
                "Arrange display order with drag-and-drop",
                "Set custom photo captions and tag outdoor/indoor views",
                "Choose your primary featured cover photo"
            ]
        },
        menu: {
            title: "Menu Items & Sections",
            icon: "solar:chef-hat-bold",
            color: "text-warning-600",
            bg: "bg-warning-50",
            border: "border-warning-100",
            summary: "Publish interactive digital menus with categories, dietary tags, prices, and dish imagery.",
            items: [
                "Organize menus into appetizers, mains, beverages, desserts, and specials",
                "Add dietary tags (Vegan, Halal, Gluten-Free, Organic)",
                "Attach appetizing food and drink photography",
                "Set prices with AED currency formatting"
            ]
        },
        features: {
            title: "Features, Amenities & Offers",
            icon: "solar:star-bold",
            color: "text-success-600",
            bg: "bg-success-50",
            border: "border-success-100",
            summary: "Highlight your venue's amenities, special dining experiences, and booking perks.",
            items: [
                "Valet Parking, Free Wi-Fi, Outdoor Terrace, Shisha Lounge",
                "Live DJ / Entertainment schedules",
                "Pet-friendly, Private Dining Rooms, Waterfront view tags",
                "Special booking perks and dress code guidelines"
            ]
        },
        blogs: {
            title: "Blog Posts & Events",
            icon: "solar:document-text-bold",
            color: "text-info-600",
            bg: "bg-info-50",
            border: "border-info-100",
            summary: "Broadcast live music nights, brunch events, guest chef takeovers, and special announcements.",
            items: [
                "Publish rich articles with imagery directly to user feeds",
                "Promote weekend brunches, ladies' nights, and holiday events",
                "Keep customers informed about seasonal menu updates",
                "Drive social discovery and in-app engagement"
            ]
        },
        vouchers: {
            title: "Vouchers & Promotional Deals",
            icon: "solar:ticket-sale-bold",
            color: "text-danger-600",
            bg: "bg-danger-50",
            border: "border-danger-100",
            summary: "Attract new visitors with exclusive discount vouchers, early bird offers, and group dining deals.",
            items: [
                "Create percentage or fixed AED discount vouchers",
                "Set validity date ranges and redemption limits",
                "Generate custom promo codes for marketing campaigns",
                "Track voucher redemption analytics in real time"
            ]
        }
    };

    return (
        <MasterLayout>
            <Breadcrumb title="Add New Venue" />

            <div className="mb-16">
                <Link
                    to={user?.role === 'admin' ? "/applications/all-venues" : "/my-venues"}
                    className="hs-btn-secondary hs-btn-sm text-decoration-none d-inline-flex align-items-center gap-2"
                >
                    <Icon icon="solar:arrow-left-linear" className="text-base" />
                    <span>Back to Venues</span>
                </Link>
            </div>

            <div className="d-flex flex-column gap-20">
                {/* 1. Header Card */}
                <div className="hs-card mb-0 shadow-sm border radius-16 bg-white overflow-hidden">
                    <div className="p-20 px-24">
                        <div className="row align-items-center gy-3">
                            <div className="col-auto">
                                <div className="w-72-px h-72-px rounded-16 bg-primary-50 text-primary-600 border border-primary-100 d-flex align-items-center justify-content-center">
                                    <Icon icon="solar:shop-2-bold" className="text-4xl" />
                                </div>
                            </div>

                            <div className="col">
                                <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                    <h4 className="mb-0 fw-bold text-dark" style={{ letterSpacing: '-0.3px' }}>
                                        New Venue Listing
                                    </h4>
                                    <span className="hs-badge hs-badge-info d-inline-flex align-items-center gap-1.5">
                                        <Icon icon="solar:pen-bold" className="text-xs" />
                                        <span>Draft</span>
                                    </span>
                                </div>
                                <p className="text-secondary-light text-xs mb-0">
                                    Complete the listing details to publish your venue across the HubScroll web directory and mobile apps.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 2. Navigation Tabs Toolbar (Same as in Edit Venue) */}
                <div className="hs-card mb-0 p-2 bg-white border radius-12 shadow-none">
                    <style>{`
                        .venue-tabs-bar {
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            width: 100%;
                            gap: 6px;
                        }
                        .venue-tab-btn {
                            flex: 1 1 0;
                            min-width: 0;
                            display: inline-flex;
                            align-items: center;
                            justify-content: center;
                            text-align: center;
                            border: 1px solid transparent;
                            background: transparent;
                            color: #334155 !important;
                            padding: 10px 14px;
                            border-radius: 9px;
                            font-size: 13px;
                            font-weight: 600;
                            transition: all 0.2s ease;
                            white-space: nowrap;
                        }
                        .venue-tab-btn:hover {
                            color: #0a2540 !important;
                            background: #f0f9ff !important;
                            border-color: #bae6fd !important;
                        }
                        .venue-tab-btn.active {
                            background: #ffffff !important;
                            border-color: #0789d5 !important;
                            color: #0789d5 !important;
                        }
                        .venue-tab-btn.is-locked {
                            color: #94a3b8 !important;
                            font-weight: 500;
                        }
                        .venue-tab-btn.is-locked svg,
                        .venue-tab-btn.is-locked .iconify {
                            color: #94a3b8 !important;
                        }
                        .venue-locked-section {
                            position: relative;
                            min-height: 520px;
                            overflow: hidden;
                            border-radius: 16px;
                            border: 1px solid #e2e8f0;
                            background: #f8fafc;
                        }
                        .venue-locked-preview {
                            position: absolute;
                            inset: 0;
                            padding: 28px;
                            filter: blur(6px);
                            opacity: 0.55;
                            pointer-events: none;
                            transform: scale(1.02);
                        }
                        .venue-locked-preview-card {
                            height: 150px;
                            border-radius: 14px;
                            border: 1px solid #dbe5ef;
                            background: linear-gradient(135deg, #ffffff, #eaf5fb);
                        }
                        .venue-locked-overlay {
                            position: relative;
                            z-index: 1;
                            min-height: 520px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            padding: 32px;
                            background: rgba(248, 250, 252, 0.58);
                            backdrop-filter: blur(2px);
                        }
                        @media (max-width: 991px) {
                            .venue-tabs-bar {
                                overflow-x: auto;
                                justify-content: flex-start;
                            }
                            .venue-tab-btn {
                                flex: 0 0 auto;
                            }
                        }
                    `}</style>
                    <div className="hs-nav-pills venue-tabs-bar">
                        {tabs.map((tab) => {
                            const isLocked = tab.id !== 'details' && !createdVenueId;
                            return (
                                <button
                                    key={tab.id}
                                    className={`hs-nav-link venue-tab-btn ${
                                        activeTab === tab.id ? 'active shadow-xs' : ''
                                    } ${isLocked ? 'is-locked' : ''}`}
                                    onClick={() => handleTabChange(tab.id)}
                                    type="button"
                                >
                                    <Icon
                                        icon={tab.icon}
                                        className="text-base flex-shrink-0"
                                        style={{ color: isLocked ? '#94a3b8' : '#0789d5' }}
                                    />
                                    <span>{tab.label}</span>
                                    {isLocked && (
                                        <span
                                            className="badge rounded-pill px-1.5 py-0.5 ms-1"
                                            style={{
                                                fontSize: '10px',
                                                backgroundColor: 'rgba(148, 163, 184, 0.12)',
                                                color: '#94a3b8'
                                            }}
                                        >
                                            <Icon icon="solar:lock-keyhole-linear" />
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* 3. Tab Content */}
                <div>
                    {activeTab === 'details' && (
                        <>
                            {!createdVenueId && (
                                <div
                                    className="radius-12 mb-20 p-16 px-20"
                                    style={{
                                        backgroundColor: 'var(--hs-info-bg, #f0f9ff)',
                                        border: '1px solid rgba(56, 189, 248, 0.35)',
                                        color: 'var(--hs-text-primary, #0f172a)'
                                    }}
                                >
                                    <strong className="d-block mb-1" style={{ color: 'var(--hs-text-primary, #0f172a)', fontSize: '14px' }}>
                                        Step 1: Create the venue profile
                                    </strong>
                                    <span className="text-sm" style={{ color: 'var(--hs-text-secondary, #334155)' }}>
                                        Complete the required fields marked with <strong>*</strong> and save the venue. Uploads and content creation stay disabled until this step is saved; Gallery, menu, features, blogs, and vouchers unlock immediately afterward.
                                    </span>
                                </div>
                            )}
                            <AddVenueLayer
                                venueId={createdVenueId}
                                onSuccess={handleSuccess}
                                onNavigateTab={handleTabChange}
                            />
                        </>
                    )}

                    {activeTab !== 'details' && !createdVenueId && (
                        <div className="venue-locked-section">
                            <div className="venue-locked-preview" aria-hidden="true" data-testid="locked-venue-preview">
                                <div className="d-flex justify-content-between align-items-center mb-24">
                                    <div className="w-50 bg-white border rounded-8" style={{ height: '42px' }} />
                                    <div className="w-25 bg-primary-100 rounded-8" style={{ height: '42px' }} />
                                </div>
                                <div className="row g-4">
                                    {[1, 2, 3, 4, 5, 6].map((item) => (
                                        <div className="col-md-4" key={item}>
                                            <div className="venue-locked-preview-card" />
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="venue-locked-overlay text-center">
                            {tabDescriptions[activeTab] && (
                                <div className="max-w-640-px mx-auto bg-white border shadow-lg radius-16 p-32">
                                    <div className={`w-80-px h-80-px rounded-circle ${tabDescriptions[activeTab].bg} ${tabDescriptions[activeTab].color} border ${tabDescriptions[activeTab].border} d-inline-flex align-items-center justify-content-center mb-20 shadow-xs`}>
                                        <Icon icon={tabDescriptions[activeTab].icon} className="text-4xl" />
                                    </div>
                                    <h4 className="fw-bold text-dark mb-12">
                                        {tabDescriptions[activeTab].title}
                                    </h4>
                                    <p className="text-secondary-light text-sm mb-24">
                                        {tabDescriptions[activeTab].summary}
                                    </p>

                                    <div className="alert alert-warning text-start mb-20 radius-10">
                                        <div className="d-flex align-items-center gap-2 fw-bold mb-2">
                                            <Icon icon="solar:lock-keyhole-bold" className="text-lg" />
                                            <span>Uploads are locked until Step 1 is saved</span>
                                        </div>
                                        <ol className="mb-0 ps-20 text-xs d-flex flex-column gap-1">
                                            <li>Return to <strong>Listing Details</strong>.</li>
                                            <li>Complete every required field marked with <strong>*</strong>.</li>
                                            <li>Select <strong>Create Venue</strong>; this section will unlock and Gallery opens automatically.</li>
                                        </ol>
                                    </div>

                                    <div className="p-20 bg-neutral-50 rounded-12 border border-neutral-200 text-start mb-28">
                                        <h6 className="text-xs fw-bold text-neutral-700 text-uppercase mb-12" style={{ letterSpacing: '0.5px' }}>
                                            What you will configure here:
                                        </h6>
                                        <ul className="mb-0 ps-20 text-xs text-neutral-600 d-flex flex-column gap-2">
                                            {tabDescriptions[activeTab].items.map((item, idx) => (
                                                <li key={idx}>{item}</li>
                                            ))}
                                        </ul>
                                    </div>

                                    <div className="alert alert-info d-flex align-items-center justify-content-center gap-2 mb-24 text-xs py-10 radius-8">
                                        <Icon icon="solar:lock-keyhole-bold" className="text-base" />
                                        <span>
                                            Please fill in and save the basic <strong>Listing Details</strong> in Step 1 to unlock this section.
                                        </span>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={() => handleTabChange('details')}
                                        className="hs-btn-primary px-24 py-12 radius-8 d-inline-flex align-items-center gap-2"
                                    >
                                        <Icon icon="solar:pen-bold" />
                                        <span>Complete Listing Details First</span>
                                    </button>
                                </div>
                            )}
                            </div>
                        </div>
                    )}

                    {/* If venue has been created in this session, render managers directly */}
                    {activeTab === 'gallery' && createdVenueId && (
                        <VenueGalleryManager venueId={createdVenueId} />
                    )}

                    {activeTab === 'menu' && createdVenueId && (
                        <VenueMenuManager venueId={createdVenueId} />
                    )}

                    {activeTab === 'features' && createdVenueId && (
                        <VenueFeaturesManager venueId={createdVenueId} />
                    )}

                    {activeTab === 'blogs' && createdVenueId && (
                        <VenueBlogsManager
                            venueId={createdVenueId}
                            isOwner={true}
                        />
                    )}

                    {activeTab === 'vouchers' && createdVenueId && (
                        <VenueVouchersManager
                            venueId={createdVenueId}
                        />
                    )}
                </div>
            </div>

            {/* Live Customer Preview Modal */}
            {createdVenueId && (
                <VenuePreviewModal
                    show={showPreview}
                    onHide={() => setShowPreview(false)}
                    venueId={createdVenueId}
                />
            )}
        </MasterLayout>
    );
};

export default AddVenuePage;
